import { authenticatedApiFetch, setAccessToken } from "./authentication";


async function throwOnErrorResponse(response) {
    let errorMessage = null
    try {
        const responseJson = await response.json();
        errorMessage = responseJson?.message;
    }
    finally {
        if (errorMessage) throw new Error(`${errorMessage}`);
        throw new Error("An unknown error has occurred");
    }
}


export class SpellBookBackend {

    static isDeployed() {
        const e = process.env.NODE_ENV;
        return e !== "development" && e !== "test";
    }

    static getServerHostname() {
        if (SpellBookBackend.isDeployed()) {
            return "spellbook.dnd.berianz.net";
        }
        else {
            return "127.0.0.1";
        }
    }

    static getBackendURL() {
        if (SpellBookBackend.isDeployed()) {
            return "https://" + SpellBookBackend.getServerHostname() + "/api";
        }
        else {
            return "http://" + SpellBookBackend.getServerHostname() + ":30050";
        }
    }

    static async createSession(username, password) {
        const res = await fetch(`${SpellBookBackend.getBackendURL()}/session`, {
            method: "POST",
            credentials: "include", // receive refreshToken cookie
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, password }),
        });
        if (res.ok) {
            const data = await res.json();
            const token = data?.content?.accessToken;
            if (!token) throw new Error("No accessToken in response");
            return token;
        }
        else {
            await throwOnErrorResponse(res);
        }
    }

    static async refreshSession() {
        const res = await fetch(`${SpellBookBackend.getBackendURL()}/session/refresh`, {
            method: "GET",
            credentials: "include", // send refreshToken cookie
        });
        if (res.ok) {
            const data = await res.json();
            const token = data?.content?.accessToken;
            if (!token) throw new Error("Failed to get (refresh) access token");
            return token;
        }
        else {
            await throwOnErrorResponse(res);
        }
    }

    static async deleteSession(accessToken) {
        const res = await fetch(`${SpellBookBackend.getBackendURL()}/session`, {
            method: "DELETE",
            credentials: "include",
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        });
        if (!res.ok) await throwOnErrorResponse(res);
    }

    static async getSessionUsername() {
        // Request the username
        var url = `${SpellBookBackend.getBackendURL()}/session/username`;
        const res = await authenticatedApiFetch(url);
        const resJson = await res.json();
        
        // Parse the response
        if (res.ok) {
            const username = resJson?.content?.username;
            if (!username) throw new Error("Failed to get username");
            return username;
        }
        else {
            await throwOnErrorResponse(res);
        }
    }

    static async changePassword(username, currentPassword, newPassword) {
        // Request the password change
        var url = `${SpellBookBackend.getBackendURL()}/change-password`;
        const res = await authenticatedApiFetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, currentPassword, newPassword }),
        });
        if (!res.ok) await throwOnErrorResponse(res);

        // Log back in
        const token = await SpellBookBackend.createSession(username, newPassword);
        setAccessToken(token);
    }

    static async getSchools() {
        // Request the schools' list
        var url = `${SpellBookBackend.getBackendURL()}/schools`;
        const res = await authenticatedApiFetch(url);
        const resJson = await res.json();
        
        // Parse the response
        if (res.ok) {
            const schools = resJson?.content?.schools;
            if (!schools) throw new Error("Failed to get schools");
            return schools;
        }
        else {
            await throwOnErrorResponse(res);
        }
    }

    static async getSchool(schoolId) {
        // Request the schools' list
        var url = `${SpellBookBackend.getBackendURL()}/schools/${schoolId}`;
        const res = await authenticatedApiFetch(url);
        const resJson = await res.json();
        
        // Parse the response
        if (res.ok) {
            const schools = resJson?.content?.schools;
            if (!schools) throw new Error(`Failed to get school ${schoolId}`);
            if (schools.length !== 1) throw new Error(`Expected exactly one school, got ${schools.length}`);
            return schools[0];
        }
        else {
            await throwOnErrorResponse(res);
        }
    }

    static async getSubSchools() {
        // Request the schools' list
        var url = `${SpellBookBackend.getBackendURL()}/sub-schools`;
        const res = await authenticatedApiFetch(url);
        const resJson = await res.json();
        
        // Parse the response
        if (res.ok) {
            const subSchools = resJson?.content?.subSchools;
            if (!subSchools) throw new Error("Failed to get sub-schools");
            return subSchools;
        }
        else {
            await throwOnErrorResponse(res);
        }
    }

    static async getDescriptors() {
        // Request the schools' list
        var url = `${SpellBookBackend.getBackendURL()}/descriptors`;
        const res = await authenticatedApiFetch(url);
        const resJson = await res.json();
        
        // Parse the response
        if (res.ok) {
            const descriptors = resJson?.content?.descriptors;
            if (!descriptors) throw new Error("Failed to get descriptors");
            return descriptors;
        }
        else {
            await throwOnErrorResponse(res);
        }
    }

    static async getClasses() {
        // Request the schools' list
        var url = `${SpellBookBackend.getBackendURL()}/caster-classes`;
        const res = await authenticatedApiFetch(url);
        const resJson = await res.json();
        
        // Parse the response
        if (res.ok) {
            const classes = resJson?.content?.casterClasses;
            if (!classes) throw new Error("Failed to get classes");
            return classes;
        }
        else {
            await throwOnErrorResponse(res);
        }
    }

    static async getClass(classId) {
        // Request the classes' list
        var url = `${SpellBookBackend.getBackendURL()}/caster-classes/${classId}`;
        const res = await authenticatedApiFetch(url);
        const resJson = await res.json();
        
        // Parse the response
        if (res.ok) {
            const classes = resJson?.content?.casterClasses;
            if (!classes) throw new Error(`Failed to get class ${classId}`);
            if (classes.length !== 1) throw new Error(`Expected exactly one class, got ${classes.length}`);
            return classes[0];
        }
        else {
            await throwOnErrorResponse(res);
        }
    }

    static async listSpellsBySchool(schoolId) {
        // Request the spells' list
        var url = `${SpellBookBackend.getBackendURL()}/schools/${schoolId}/spells-list`;
        const res = await authenticatedApiFetch(url);
        const resJson = await res.json();
        
        // Parse the response
        if (res.ok) {
            const spells = resJson?.content?.spells;
            if (!spells) throw new Error("Failed to get spells");
            return spells;
        }
        else {
            await throwOnErrorResponse(res);
        }
    }

    static async listSpellsByClass(classId) {
        // Request the spells' list
        var url = `${SpellBookBackend.getBackendURL()}/caster-classes/${classId}/spells-list`;
        const res = await authenticatedApiFetch(url);
        const resJson = await res.json();
        
        // Parse the response
        if (res.ok) {
            const spells = resJson?.content?.spells;
            if (!spells) throw new Error("Failed to get spells");
            return spells;
        }
        else {
            await throwOnErrorResponse(res);
        }
    }

    static async listSpells() {
        // Request the spells' list
        var url = `${SpellBookBackend.getBackendURL()}/spells-list`;
        const res = await authenticatedApiFetch(url);
        const resJson = await res.json();
        
        // Parse the response
        if (res.ok) {
            const spells = resJson?.content?.spells;
            if (!spells) throw new Error("Failed to get spells");
            return spells;
        }
        else {
            await throwOnErrorResponse(res);
        }
    }

    static async getSpell(spellId) {
        // Request the spells' list
        var url = `${SpellBookBackend.getBackendURL()}/spells/${spellId}`;
        const res = await authenticatedApiFetch(url);
        const resJson = await res.json();
        
        // Parse the response
        if (res.ok) {
            const spells = resJson?.content?.spells;
            if (!spells) throw new Error("Failed to get spells");
            if (spells.length > 1) throw new Error(`Multiple spells returned for ID '${spellId}'`)
            return spells.length > 0 ? spells[0] : null;
        }
        else {
            await throwOnErrorResponse(res);
        }
    }

    static composeIncludedExcludedSpellFilter(type, included=null, excluded=null) {
        let filter = {
            type: type
        };
        if (included && included.length > 0) filter["included"] = included.map((x) => x.id);
        if (excluded && excluded.length > 0) filter["excluded"] = excluded.map((x) => x.id);
        return filter;
    }

    static composeCasterSpellFilter(casterClassId, levels=null) {
        let filter = {
            type: "Caster",
            casterClass: casterClassId
        };
        if (levels && levels.length > 0) filter["levels"] = levels;
        return filter;
    }

    static composeSpellResistanceSpellFilter(spellResistance) {
        return {
            type: "SpellResistance",
            spellResistance: spellResistance
        };
    }

    static composePatternMatchSpellFilter(type, pattern, matchCase=false) {
        let filter = {
            type: type,
            pattern: RegExp.escape(pattern)
        };
        if (matchCase === true) filter["matchCase"] = true;
        return filter;
    }

    static composeAndSpellFilter(childFilters) {
        return {
            type: "And",
            filters: childFilters
        }
    }

    static composeOrSpellFilter(childFilters) {
        return {
            type: "Or",
            filters: childFilters
        }
    }

    static async searchSpells(query, searchInBrief=true, searchInDescription=true, includedSchools=null, includedSubSchools=null, includedDescriptors=null, includedClasses=null, withSpellResistance=null) {
        // Compose the search filters
        let searchFilters = [];
        if (includedSchools && includedSchools.length > 0) {
            searchFilters.push(SpellBookBackend.composeIncludedExcludedSpellFilter("SchoolMatch", includedSchools));
        }
        if (includedSubSchools && includedSubSchools.length > 0) {
            searchFilters.push(SpellBookBackend.composeIncludedExcludedSpellFilter("SubSchoolMatch", includedSubSchools));
        }
        if (includedDescriptors && includedDescriptors.length > 0) {
            searchFilters.push(SpellBookBackend.composeIncludedExcludedSpellFilter("DescriptorMatch", includedDescriptors));
        }
        if (includedClasses && includedClasses.length > 0) {
            let classesSearchFilters = [];
            for (const c of includedClasses) {
                classesSearchFilters.push(SpellBookBackend.composeCasterSpellFilter(c.id));
            }
            searchFilters.push(classesSearchFilters.length > 1 ? SpellBookBackend.composeOrSpellFilter(classesSearchFilters) : classesSearchFilters[0]);
        }
        if (withSpellResistance != null) {
            searchFilters.push(SpellBookBackend.composeSpellResistanceSpellFilter(withSpellResistance === false ? false : true));
        }
        if (query && query.length > 0) {
            let querySearchFilters = [
                SpellBookBackend.composePatternMatchSpellFilter("NamePatternMatch", query)
            ];
            if (searchInBrief) querySearchFilters.push(SpellBookBackend.composePatternMatchSpellFilter("BriefPatternMatch", query));
            if (searchInDescription) querySearchFilters.push(SpellBookBackend.composePatternMatchSpellFilter("DescriptionPatternMatch", query));
            searchFilters.push(querySearchFilters.length > 1 ? SpellBookBackend.composeOrSpellFilter(querySearchFilters) : querySearchFilters[0]);
        }

        // Check for no filters
        if (searchFilters.length === 0) {
            // Return all the spells
            return await SpellBookBackend.listSpells();
        }
        else {
            // Compose the final filter
            const searchFilter = {
                "filter": searchFilters.length > 1 ? SpellBookBackend.composeAndSpellFilter(searchFilters) : searchFilters[0]
            };

            // Initializations
            let resultSpells = [];

            // Start the search
            const startSearchUrl = `${SpellBookBackend.getBackendURL()}/search/spells`;
            const startSearchRes = await authenticatedApiFetch(startSearchUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(searchFilter)
            });
            if (!startSearchRes.ok) await throwOnErrorResponse(startSearchRes);
            const startSearchJson = await startSearchRes.json();
            const searchId = startSearchJson?.content?.searchId;
            if (!searchId) throw new Error("Failed to start search");

            // Run the search until finished
            let searchFinished = false;
            const searchUrl = `${SpellBookBackend.getBackendURL()}/search/spells/${searchId}`;
            while (!searchFinished) {
                // Run the search
                const searchRes = await authenticatedApiFetch(searchUrl);
                if (!searchRes.ok) await throwOnErrorResponse(searchRes);
                const searchJson = await searchRes.json();

                // Append the found spells
                const spells = searchJson?.content?.spells;
                if (spells && spells.length > 0) {
                    resultSpells.push(...spells);
                }

                // Check if the search has finished
                if (searchJson?.content?.finished === true) searchFinished = true;
            }
            
            // Return the found spells
            return resultSpells;
        }
    }
}